import { Field, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import * as Yup from 'yup';
import miciCasesService from '../../../../services/miciCasesService';
import { MiciCaseMiciCause } from '../../../../services/types/MiciCases';
import { CustomDropDown, Modal } from '../../../../shared/components';
import DropdownChecks from '../../../../shared/components/DropdownChecks';
import Context from '../MiciCaseContext';

export const otherReasonId = '13';
interface FormState {
  causes: string[];
  otherReason?: string;
}

interface Props {
  visible: boolean;
  setVisible: (value: boolean) => void;
}

export const buildMiciCausesSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    causes: Yup.array(),
    otherReason: Yup.string().when('causes', {
      is: (s: string[] | undefined) => s && s.find((x) => x === otherReasonId),
      then: (schema) => schema.concat(Yup.string().required(t('required'))),
    }),
  });

const MiciCaseForm: React.FC<Props> = ({ visible, setVisible }) => {
  const { t } = useTranslation();
  const {
    data: { miciCase, miciCauses },
    reload,
  } = useContext(Context);

  const initialOtherReason = miciCase.miciCauses.find(
    (x) => x.miciCauseId === Number(otherReasonId)
  );

  const formInit: FormState = {
    causes: miciCase.miciCauses.map((x) => x.miciCauseId.toString()),
    otherReason: initialOtherReason ? initialOtherReason.otherReason : '',
  };

  const formSchema = buildMiciCausesSchema(t);
  const [causes, setCauses] = useState<string[]>(
    miciCase.miciCauses.map((x) => x.miciCauseId.toString())
  );
  useEffect(() => {
    setCauses(miciCase.miciCauses.map((x) => x.miciCauseId.toString()));
  }, [visible]);

  const setCauseSelected = (id: string, checked: boolean, formik: FormikProps<FormState>) => {
    let newValues;
    if (!checked) {
      newValues = [...causes].filter((x) => x !== id);
    } else {
      newValues = [...causes, id];
    }
    if (id === otherReasonId) {
      formik.setFieldValue('otherReason', '');
    }
    setCauses(newValues);
    formik.setFieldValue('causes', newValues);
    setTimeout(() => {
      formik.validateForm();
    });
  };
  // Formik's resetForm() already invokes this onReset handler itself (see
  // formik/dist/formik.cjs.development.js) - calling resetForm() again from
  // in here re-enters it and recurses forever. handleReset (bound to the
  // form's native reset event) already calls resetForm() before this runs,
  // so nothing here needs to reset the form again.
  const handleReset = async () => {
    setVisible(false);
  };

  const handleSubmit = async (values: FormState, formikHelpers: FormikHelpers<FormState>) => {
    const { resetForm } = formikHelpers;
    const payload: MiciCaseMiciCause[] = causes.map((x) => ({
      miciCaseId: miciCase.id,
      miciCauseId: Number(x),
      otherReason: x === otherReasonId ? values.otherReason : undefined,
    }));
    await miciCasesService.saveCauses(miciCase.id, payload);
    reload();
    setVisible(false);
    resetForm();
  };

  return (
    <Modal title={t('causes')} visible={visible} setVisible={() => setVisible(false)}>
      <Formik
        initialValues={formInit}
        validationSchema={formSchema}
        onSubmit={async (values, formikHelpers) => {
          await handleSubmit(values, formikHelpers);
        }}
        onReset={async () => {
          await handleReset();
        }}
      >
        {(formik) => (
          <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <div className="modal__body">
              <div className="spr-modal modal-dialog--custom-filter">
                <div className="form-row">
                  <div className="col-sm-12">
                    <CustomDropDown
                      text={
                        causes.length >= 1
                          ? `${causes.length} ${t('selected')}`
                          : t('selectTheOptions')
                      }
                      className="custom-filters-dropdown mt-1"
                    >
                      <DropdownChecks
                        elements={miciCauses.map((x) => ({
                          id: x.id.toString(),
                          name: x.description,
                        }))}
                        selected={causes}
                        setSelected={(id, checked) => {
                          setCauseSelected(id, checked, formik);
                        }}
                      />
                    </CustomDropDown>
                  </div>
                  {formik.touched.causes && formik.errors.causes && (
                    <div className="col-sm-12">
                      <span className="form__form-group-error">{formik.errors.causes}</span>
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="col-sm-12">
                    <label className="col-form-label" htmlFor="otherReason">
                      {t('otherReason')}
                    </label>
                    <Field
                      as="textarea"
                      id="otherReason"
                      name="otherReason"
                      maxLength={100}
                      disabled={!causes.find((x) => x === otherReasonId)}
                    />
                  </div>
                  {formik.touched.otherReason && formik.errors.otherReason && (
                    <div className="col-sm-12">
                      <span className="form__form-group-error">{formik.errors.otherReason}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal__footer modal__foote--custom-filter">
              <Button key="btnCancel" className="btn-tertiary" type="reset">
                {t('cancel')}
              </Button>
              <Button
                key="btnSave"
                className="btn-primary"
                type="submit"
                disabled={!formik.isValid}
                color="primary"
              >
                {t('apply')}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default MiciCaseForm;
